import { Link, useParams, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { useSchoolBootstrap } from "@/hooks/use-school-bootstrap";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

/**
 * LRN-15: during the deletion grace period the account can't sign in, so cancelling is a public
 * page that proves ownership with email + password. Reached after requesting deletion
 * (?scheduled=1) and from the login page when a pending-deletion account signs in.
 */
export function TenantAccountRestorePage() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const [params] = useSearchParams();
  const t = useTranslations("privacy.restore");
  const tRoot = useTranslations();
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [restored, setRestored] = useState(false);
  const scheduled = params.get("scheduled") === "1";
  // MT-1 S22 (FR-44.11): this page restores the account at the URL's school only, and says so.
  const { tenant } = useSchoolBootstrap(tenantSlug);
  const school = tenant?.name ?? tenantSlug;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch("/v1/privacy/cancel-deletion", {
        method: "POST",
        body: JSON.stringify({ email, password, tenantSlug }),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      setRestored(true);
    } catch (err) {
      setError(apiErrorText(err, tRoot, "privacy.restore.failed", { namespace: "privacy.errors" }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto max-w-md px-4 py-10" data-testid="account-restore">
      <h1 className="text-2xl font-semibold">{t("title", { school })}</h1>
      {scheduled ? (
        <p role="status" className="mt-3 text-sm" data-testid="account-restore-scheduled">
          {t("scheduled", { school })}
        </p>
      ) : (
        <p className="mt-3 text-sm text-[var(--text-muted)]">{t("pending", { school })}</p>
      )}

      {restored ? (
        <div className="mt-6 space-y-3" data-testid="account-restore-done" role="status">
          <p>{t("done", { school })}</p>
          <Link to={`/t/${tenantSlug}/login`} className="text-[var(--text-heading)] underline">
            {t("signIn")}
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <p className="text-sm text-[var(--text-secondary)]">{t("instructions")}</p>
          <label className="block text-sm">
            <span className="font-medium">{t("email")}</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-[var(--border-strong)] px-3 py-2"
              data-testid="account-restore-email"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">{t("password")}</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-md border border-[var(--border-strong)] px-3 py-2"
              data-testid="account-restore-password"
            />
          </label>
          {error ? (
            <p
              role="alert"
              className="text-sm text-[var(--danger)]"
              data-testid="account-restore-error"
            >
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={busy}
            className="rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white disabled:opacity-50"
            data-testid="account-restore-submit"
          >
            {busy ? t("working") : t("submit")}
          </button>
        </form>
      )}
    </main>
  );
}
